/**
 * FEMA National Flood Hazard Layer (NFHL).
 *
 * Boundary sites first query NFHL flood-hazard polygons over the parcel
 * envelope and intersect the returned geometry with the actual parcel. The
 * result reports true planimetric acreage/fractions. If FEMA truncates the
 * response, returns malformed geometry, or all polygon endpoints fail, the
 * provider falls back to the legacy 3x3 + centroid sweep and labels that result
 * as an estimate. Address-only legacy sites retain the original point query.
 */
import polygonClipping from 'polygon-clipping';
import type { FemaRegulatoryContext } from '../../types/femaRegulatoryContext';
import { projectFemaRegulatoryContext } from './femaRegulatoryContext';
import type { FemaSourceContextBinding } from '../../types/refdata';
import { femaSourceContext, type FemaSourceContextAssessment } from './femaSourceContext';
import { GEOMETRY_PRECISION_SNAP_DEG, GEOMETRY_PRECISION_SNAP_MAX_SHIFT_MM, type FloodScreeningCoverage } from '../../types/floodScreening';
export { GEOMETRY_PRECISION_SNAP_DEG, GEOMETRY_PRECISION_SNAP_MAX_SHIFT_MM };
import { polygonArea } from '../../lib/siteBoundaryGeometry';
import { rethrowIfAborted, throwIfAborted } from '../../lib/abortSignal';
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import {
  CACHE_TTL,
  boundaryFingerprint,
  fetchWithTimeout,
  gridCellKey,
  multiBoundaryFingerprint,
} from './types';
import { refdataBbox, refdataContains, tryRefdata, isPrivateRefdataAcceptanceDataset } from '../refdata/refdataClient';

export type FloodAnalysisMethod = 'polygon-intersection' | 'sample-sweep' | 'point';
export type FloodAnnualChanceClass =
  | '1-percent'
  | '0.2-percent'
  | 'minimal'
  | 'undetermined';

export interface FloodZoneArea {
  zone: string;
  annualChance: FloodAnnualChanceClass;
  acres: number;
  fraction: number;
  sfha: boolean;
  /** True when at least one intersecting NFHL feature for this zone is a
   * regulatory floodway. Use `floodwayAcres` for the exact floodway extent. */
  floodway: boolean;
}

export interface FloodAreaAnalysis {
  method: 'polygon-intersection';
  parcelAreaAcres: number;
  mappedAreaAcres: number;
  unmappedAreaAcres: number;
  mappedFraction: number;
  unmappedFraction: number;
  onePercentAnnualChanceAcres: number;
  onePercentAnnualChanceFraction: number;
  zeroPointTwoPercentAnnualChanceAcres: number;
  zeroPointTwoPercentAnnualChanceFraction: number;
  floodwayAcres: number;
  floodwayFraction: number;
  zoneAreas: FloodZoneArea[];
  /** |mapped + unmapped - parcel| / parcel. Must remain <= 0.001 for an
   * exact result; otherwise the caller falls back to sample screening. */
  closureErrorFraction: number;
  /** Present only when the exact pairwise computation failed inside
   * polygon-clipping and a disclosed fallback tier produced this result. */
  geometryTier?: FemaGeometryTier;
  /** Present only with geometryTier 'precision-snap': the fixed coordinate
   * grid (degrees) the analysis was recomputed on. */
  precisionSnapDeg?: number;
  /** Present only when a fallback tier erased a positive sliver that an exact
   * per-feature clip still finds: the largest such clip (acres), used as the
   * lower bound of the 1%-annual-chance acreage. */
  exactPositiveFloorAcres?: number;
  /** Positive features whose own exact clip failed and were left out of the
   * floor; present only when non-zero, so the lower bound is read as partial. */
  exactPositiveFloorSkippedFeatures?: number;
  /** Present only when a fallback tier understated the regulatory floodway:
   * the largest exactly clipped floodway feature (acres), the floodway floor. */
  exactFloodwayFloorAcres?: number;
  /** Present only when a fallback tier reported no SFHA exposure but an exact
   * per-feature clip found some: the SFHA flag, risk and headline zone were
   * restored while the acreage stayed within the gate tolerance. */
  exactPositiveHeadlineRestored?: true;
  /** An exact positive source flag disagrees with its declared chance class.
   * Retained in the provider citation and coverage reason, including after
   * composition with a separate coverage audit. It never proves 1% acreage. */
  exactPositiveClassFloorDisclosure?: string;
}

export interface FloodQueryAudit {
  regulatoryContext?: FemaRegulatoryContext;
  source: 'refdata-snapshot' | 'live-nfhl';
  truncated?: boolean;
  screeningCoverage?: FloodScreeningCoverage;
  boundaryFingerprint: string;
  snapshotVersion?: string;
  snapshotVintageDate?: string;
  snapshotAuthority?: string;
  localMappedFraction?: number;
  liveAttemptedAt?: string;
  liveOutcome?: 'more-complete-coverage' | 'additional-source-evidence' | 'unavailable-or-no-coverage-improvement';
  /** Present when the live body was admitted on a disclosed geometry fallback tier. */
  liveGeometryTier?: FemaGeometryTier;
  retainedSnapshotEvidence?: boolean;
  liveEndpoint?: string;
  femaContext?: { status: FemaSourceContextAssessment['status']; reason: string; binding?: FemaSourceContextBinding; counts: Record<string, number> };
}

export interface FloodData {
  regulatoryContext?: FemaRegulatoryContext;
  /** Highest-risk zone intersecting the parcel (or point). */
  femaZone: string;
  /** True when any parcel acreage intersects the 1% annual-chance SFHA. */
  isSpecialFloodHazard: boolean;
  floodRisk: 'Low' | 'Low-Medium' | 'Medium' | 'High';
  /** Back-compatible zone fractions. Exact polygon results use true area;
   * fallback results use sample counts. UNMAPPED is included when present. */
  zoneOverlap?: { zone: string; fraction: number }[];
  /** Number of points queried for point/sweep analysis. Omitted for exact area. */
  sampledPoints?: number;
  analysisMethod?: FloodAnalysisMethod;
  areaAnalysis?: FloodAreaAnalysis;
  queryAudit?: FloodQueryAudit;
  screeningCoverage?: FloodScreeningCoverage;
  /** Present only when polygon analysis could not be trusted and the sample
   * sweep was used instead. */
  fallbackReason?: string;
  /** Populated only when the intersecting NFHL geometry supplies one unique,
   * finite static BFE and datum/panel metadata is unambiguous. */
  staticBfeFt?: number;
  verticalDatum?: 'NAVD88' | 'NGVD29' | 'MLLW';
  firmPanelId?: string;
  effectiveDate?: string;
}

/** Map FEMA zone codes to risk levels. */
export function zoneToRisk(zone: string): FloodData['floodRisk'] {
  const z = zone.toUpperCase();
  if (['A', 'AE', 'AH', 'AO', 'A99', 'AR', 'V', 'VE'].includes(z)) return 'High';
  if (['B', 'X500', '0.2 PCT ANNUAL CHANCE FLOOD HAZARD'].some(k => z.includes(k))) return 'Medium';
  if (z === 'C' || z === 'X' || z.includes('MINIMAL')) return 'Low';
  return 'Low-Medium';
}

const RISK_ORDER: Record<FloodData['floodRisk'], number> = {
  Low: 0,
  'Low-Medium': 1,
  Medium: 2,
  High: 3,
};
const AREA_CLOSURE_TOLERANCE = 0.001;
const AREA_EPSILON_ACRES = 1e-6;
const NFHL_OUT_FIELDS = [
  'FLD_ZONE',
  'ZONE_SUBTY',
  'SFHA_TF',
  'STATIC_BFE',
  'V_DATUM',
  'LEN_UNIT',
  'DFIRM_ID',
].join(',');
const NFHL_ENDPOINTS = [
  'https://hazards.fema.gov/gis/nfhl/rest/services/public/NFHL/MapServer/28/query',
  'https://hazards.fema.gov/arcgis/rest/services/public/NFHL/MapServer/28/query',
] as const;
const NFHL_POINT_ENDPOINTS = [
  ...NFHL_ENDPOINTS,
  'https://hazards.fema.gov/gis/nfhl/rest/services/public/NFHL/MapServer/14/query',
] as const;

interface FemaAttributes {
  /** Frozen source identity; a legacy positive never supplies native negative coverage. */
  SOURCE_ROLE?: 'official-native' | 'legacy-screening-derivative' | 'official-live';
  SOURCE_VERSION?: string;
  SOURCE_SHA256?: string;
  /** Explicit unavailable envelope, never an observed flood polygon. */
  STATUS?: string;
  /** Audit-only native invalid geometry must not become usable via clipping. */
  nativeValid?: boolean;
  FLD_ZONE?: string;
  ZONE_SUBTY?: string;
  SFHA_TF?: string;
  STATIC_BFE?: number | string;
  V_DATUM?: string;
  LEN_UNIT?: string;
  DFIRM_ID?: string;
  PANEL?: string;
  FIRM_PAN?: string;
  EFF_DATE?: number | string;
}

export interface FemaPolygonFeature {
  attributes: FemaAttributes;
  geometry?: { rings?: number[][][] };
}

interface FemaPointResult {
  regulatoryContext?: FemaRegulatoryContext;
  zone: string;
  sfha: boolean;
  screeningCoverage: FloodScreeningCoverage;
}

type Position = [number, number];
type MultiPolygon = polygonClipping.MultiPolygon;

export interface FemaParcelPolygonInput {
  id?: string;
  polygon: ReadonlyArray<{ lat: number; lng: number }>;
  holes?: ReadonlyArray<ReadonlyArray<{ lat: number; lng: number }>>;
}

export type FemaBoundaryInput =
  | ReadonlyArray<{ lat: number; lng: number }>
  | ReadonlyArray<FemaParcelPolygonInput>;

function isParcelPolygonInputArray(
  input: FemaBoundaryInput,
): input is ReadonlyArray<FemaParcelPolygonInput> {
  return input.length > 0 && typeof input[0] === 'object' && input[0] !== null && 'polygon' in input[0];
}

function boundaryPoints(input: FemaBoundaryInput): { lat: number; lng: number }[] {
  if (isParcelPolygonInputArray(input)) {
    return input.flatMap(part => [...part.polygon, ...(part.holes ?? []).flat()]);
  }
  return [...input];
}

function round(value: number, digits = 6): number {
  const scale = 10 ** digits;
  return Math.round(value * scale) / scale;
}

function clampFraction(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function closeLngLatRing(points: ReadonlyArray<{ lat: number; lng: number }>): Position[] {
  const ring: Position[] = points
    .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng))
    .map(p => [p.lng, p.lat]);
  if (ring.length >= 3) {
    const first = ring[0]!;
    const last = ring[ring.length - 1]!;
    if (first[0] !== last[0] || first[1] !== last[1]) ring.push([...first]);
  }
  return ring;
}

function normalizeArcRing(raw: number[][]): Position[] | null {
  const ring = raw
    .filter(p => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]))
    .map(p => [Number(p[0]), Number(p[1])] as Position);
  if (ring.length < 3) return null;
  const first = ring[0]!;
  const last = ring[ring.length - 1]!;
  if (first[0] !== last[0] || first[1] !== last[1]) ring.push([...first]);
  return ring.length >= 4 ? ring : null;
}

function signedPlanarArea(ring: Position[]): number {
  let area = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const a = ring[i]!;
    const b = ring[i + 1]!;
    area += a[0] * b[1] - b[0] * a[1];
  }
  return area / 2;
}

/** Structurally valid rings (finite pairs, at least three points each) whose
 * every ring has zero planar area. Cell-clipped source intersections can emit
 * such slivers; they carry no acreage and are not malformed geometry. */
function isZeroAreaRingSet(rawRings: number[][][]): boolean {
  if (!Array.isArray(rawRings) || rawRings.length === 0) return false;
  if (rawRings.some(raw => !Array.isArray(raw) || raw.length < 3 || raw.some(point =>
    !Array.isArray(point) || point.length < 2 || !Number.isFinite(point[0]) || !Number.isFinite(point[1])))) return false;
  return rawRings.every(raw => {
    const ring = normalizeArcRing(raw);
    return ring !== null && Math.abs(signedPlanarArea(ring)) === 0;
  });
}

function pointInRing(point: Position, ring: Position[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i]!;
    const b = ring[j]!;
    const intersects =
      (a[1] > point[1]) !== (b[1] > point[1]) &&
      point[0] < ((b[0] - a[0]) * (point[1] - a[1])) / (b[1] - a[1]) + a[0];
    if (intersects) inside = !inside;
  }
  return inside;
}

/** Convert ArcGIS's flat rings array into GeoJSON-style multipolygon nesting.
 * Ring orientation is deliberately ignored; containment depth determines
 * outer/hole/island topology, which handles reversed rings safely. */
export function arcgisRingsToMultiPolygon(rawRings: number[][][]): MultiPolygon | null {
  if (!Array.isArray(rawRings) || rawRings.length === 0 || rawRings.some(raw =>
    !Array.isArray(raw) || raw.length < 3 || raw.some(point =>
      !Array.isArray(point) || point.length < 2 || !Number.isFinite(point[0]) || !Number.isFinite(point[1])))) return null;
  const rings = rawRings
    .map(normalizeArcRing)
    .filter((ring): ring is Position[] => ring !== null)
    .map((ring, originalIndex) => ({
      ring,
      originalIndex,
      absArea: Math.abs(signedPlanarArea(ring)),
      parent: -1,
      depth: 0,
    }))
    .filter(node => node.absArea > 0)
    .sort((a, b) => b.absArea - a.absArea);
  if (rings.length === 0) return null;

  for (let i = 0; i < rings.length; i++) {
    const child = rings[i]!;
    let smallestParentArea = Number.POSITIVE_INFINITY;
    for (let j = 0; j < i; j++) {
      const candidate = rings[j]!;
      if (candidate.absArea >= smallestParentArea) continue;
      if (pointInRing(child.ring[0]!, candidate.ring)) {
        child.parent = j;
        child.depth = candidate.depth + 1;
        smallestParentArea = candidate.absArea;
      }
    }
  }

  const polygons: polygonClipping.Polygon[] = [];
  const polygonByNode = new Map<number, polygonClipping.Polygon>();
  for (let i = 0; i < rings.length; i++) {
    const node = rings[i]!;
    if (node.depth % 2 !== 0) continue;
    const polygon = [node.ring] as polygonClipping.Polygon;
    polygons.push(polygon);
    polygonByNode.set(i, polygon);
  }
  for (let i = 0; i < rings.length; i++) {
    const node = rings[i]!;
    if (node.depth % 2 === 0 || node.parent < 0) continue;
    const parent = polygonByNode.get(node.parent);
    if (parent) parent.push(node.ring);
  }
  return polygons;
}

function multiPolygonAreaAcres(geom: MultiPolygon): number {
  let acres = 0;
  for (const poly of geom) {
    const outer = poly[0];
    if (!outer || outer.length < 4) continue;
    acres += polygonArea(outer.map(([lng, lat]) => ({ lat, lng })));
    for (let i = 1; i < poly.length; i++) {
      const hole = poly[i];
      if (hole && hole.length >= 4) {
        acres -= polygonArea(hole.map(([lng, lat]) => ({ lat, lng })));
      }
    }
  }
  return Math.max(0, acres);
}

/** Geometry fallback tiers. 'exact' is the historical pairwise-fold
 * computation and is bit-identical to earlier releases. When polygon-clipping
 * (0.15.7, fixed Number.EPSILON, no precision API) cannot reconstruct an output
 * ring, the same set union is retried in ONE sweep at exact precision
 * ('single-sweep'); if that still fails, every coordinate is snapped to a fixed
 * 1e-9-degree grid ('precision-snap'). Both fallback tiers are disclosed on the
 * result and neither may claim complete coverage; neither is applied when the
 * exact computation succeeds. */
export type FemaGeometryTier = 'single-sweep' | 'precision-snap';
const PRECISION_SNAP_NOTE = ` Geometry operations were completed on a ${GEOMETRY_PRECISION_SNAP_DEG}-degree coordinate grid (each vertex moved by at most ${GEOMETRY_PRECISION_SNAP_MAX_SHIFT_MM} mm) after an exact-precision clipping failure; complete coverage is not claimed at this precision.`;
const SINGLE_SWEEP_NOTE = ' Geometry unions were completed in a single sweep after an exact-precision pairwise clipping failure; complete coverage is not claimed on this fallback path.';
const tierNote = (tier: FemaGeometryTier): string => tier === 'precision-snap' ? PRECISION_SNAP_NOTE : SINGLE_SWEEP_NOTE;
function snapValue(value: number): number { return Math.round(value / GEOMETRY_PRECISION_SNAP_DEG) * GEOMETRY_PRECISION_SNAP_DEG; }
function snapBoundary(points: FemaBoundaryInput): FemaBoundaryInput {
  if (isParcelPolygonInputArray(points)) {
    return points.map(part => ({
      ...part,
      polygon: part.polygon.map(point => ({ lat: snapValue(point.lat), lng: snapValue(point.lng) })),
      ...(part.holes?.length
        ? { holes: part.holes.map(hole => hole.map(point => ({ lat: snapValue(point.lat), lng: snapValue(point.lng) }))) }
        : {}),
    }));
  }
  return points.map(point => ({ lat: snapValue(point.lat), lng: snapValue(point.lng) }));
}
export function snapMultiPolygonPrecision(geometry: MultiPolygon): MultiPolygon {
  return geometry.map(polygon => polygon.map(ring => ring.map(point => [snapValue(point[0]!), snapValue(point[1]!)] as Position)));
}
function snapFeaturePrecision(feature: FemaPolygonFeature): FemaPolygonFeature {
  const rings = feature.geometry?.rings;
  if (!rings) return feature;
  return { ...feature, geometry: { ...feature.geometry, rings: rings.map(ring => ring.map(point => [snapValue(point[0]!), snapValue(point[1]!)])) } };
}
/** polygon-clipping 0.15.7's ring-reconstruction failure message; every other
 * error keeps failing closed. Pinned to the library's wording. */
export function isClippingRingFailure(error: unknown): boolean {
  return error instanceof Error && /Unable to complete output ring/.test(error.message);
}

/** Set union of every operand. `sweep=false` is the historical pairwise fold
 * (bit-identical to earlier releases); `sweep=true` unions all operands in one
 * polygon-clipping sweep, which accumulates fewer floating-point artefacts. */
function unionMany(geometries: MultiPolygon[], sweep = false): MultiPolygon {
  if (!sweep) {
    if (geometries.length === 0) return [];
    let merged = geometries[0]!;
    for (let i = 1; i < geometries.length; i++) {
      merged = polygonClipping.union(merged, geometries[i]!) as MultiPolygon;
    }
    return merged;
  }
  const operands = geometries.filter(geometry => geometry.length > 0);
  if (operands.length === 0) return [];
  if (operands.length === 1) return operands[0]!;
  return polygonClipping.union(operands[0]!, ...operands.slice(1)) as MultiPolygon;
}

function toParcelMultiPolygon(
  input: FemaBoundaryInput,
  sweep = false,
): { geom: MultiPolygon; acres: number; rings: Position[][] } | null {
  if (!isParcelPolygonInputArray(input)) {
    const ring = closeLngLatRing(input);
    const acres = polygonArea(input);
    if (ring.length < 4 || !Number.isFinite(acres) || acres <= 0) return null;
    return { geom: [[ring]] as MultiPolygon, acres, rings: [ring] };
  }
  if (input.length === 1 && (!input[0]!.holes || input[0]!.holes.length === 0)) {
    const single = input[0]!.polygon;
    const ring = closeLngLatRing(single);
    const acres = polygonArea(single);
    if (ring.length < 4 || !Number.isFinite(acres) || acres <= 0) return null;
    return { geom: [[ring]] as MultiPolygon, acres, rings: [ring] };
  }
  const polys: MultiPolygon[] = [];
  for (const part of input) {
    const outer = closeLngLatRing(part.polygon);
    if (outer.length < 4 || polygonArea(part.polygon) <= 0) return null;
    const holeRings: Position[][] = [];
    for (const hole of part.holes ?? []) {
      const hr = closeLngLatRing(hole);
      if (hr.length < 4 || polygonArea(hole) <= 0) return null;
      holeRings.push(hr);
    }
    polys.push([[outer, ...holeRings]]);
  }
  if (polys.length === 0) return null;
  const geom = polys.length === 1 ? polys[0]! : unionMany(polys, sweep);
  const acres = multiPolygonAreaAcres(geom);
  if (!Number.isFinite(acres) || acres <= 0) return null;
  const rings = geom.flatMap(poly => poly.map(r => r as Position[]));
  return { geom, acres, rings };
}

export function annualChanceClass(zone: string, subtype: string, sfha: boolean): FloodAnnualChanceClass {
  const z = zone.toUpperCase();
  const st = subtype.toUpperCase();
  if (sfha || /^(A|V)/.test(z)) return '1-percent';
  if (z === 'B' || z.includes('X500') || st.includes('0.2 PCT') || st.includes('0.2 PERCENT')) {
    return '0.2-percent';
  }
  if (z === 'C' || z === 'X' || st.includes('MINIMAL')) return 'minimal';
  return 'undetermined';
}

/** NFHL commonly encodes both minimal-risk and 0.2%-annual-chance areas as
 * FLD_ZONE=X and distinguishes them only with ZONE_SUBTY. Preserve the
 * legacy X500 identifier for the latter so risk ranking and reporting do not
 * collapse a 500-year area into minimal-risk Zone X. */
function normalizeFloodZone(rawZone: unknown, subtype: string): string {
  const zone = String(rawZone ?? 'UNKNOWN').trim().toUpperCase() || 'UNKNOWN';
  const normalizedSubtype = subtype.toUpperCase();
  if (
    zone === 'X'
    && (normalizedSubtype.includes('0.2 PCT') || normalizedSubtype.includes('0.2 PERCENT'))
  ) {
    return 'X500';
  }
  return zone;
}

export function isFloodway(subtype: string): boolean {
  return subtype.toUpperCase().includes('FLOODWAY');
}

/** An unavailable envelope can carry original hazard attributes for audit;
 * those attributes do not turn its conservative envelope into hazard geometry. */
export function isFemaSourceUnavailable(attrs: FemaAttributes): boolean {
  return attrs.STATUS === 'Source_Unavailable' || attrs.nativeValid === false;
}

function floodClassification(attrs: FemaAttributes) {
  const subtype = String(attrs.ZONE_SUBTY ?? '');
  const zone = normalizeFloodZone(attrs.FLD_ZONE, subtype);
  const flag = String(attrs.SFHA_TF ?? '').toUpperCase();
  const zoneClass = annualChanceClass(zone, subtype, false);
  const positive = flag === 'T' || zoneClass === '1-percent';
  const negative = flag === 'F' || zoneClass === 'minimal' || zoneClass === '0.2-percent';
  return { zone, positive, negative, known: zoneClass !== 'undetermined', conflict: positive && negative, legacy: attrs.SOURCE_ROLE === 'legacy-screening-derivative' };
}

/** Order-independent reduction of every record at the query point. */
export function summarizeFemaPoint(
  attributes: ReadonlyArray<FemaAttributes>,
  truncated = false,
  contextConflict = false,
): FemaPointResult {
  const available = attributes.filter(attrs => !isFemaSourceUnavailable(attrs));
  const rows = available.map(floodClassification);
  const positive = rows.some(row => row.positive);
  const conflict = contextConflict || (positive && rows.some(row => row.negative && !row.legacy));
  const unknown = truncated || available.length !== attributes.length
    || rows.length === 0 || rows.some(row => !row.known)
    || !rows.some(row => row.known && !row.legacy);
  const observedZones = [...new Set(rows.map(row => row.zone))].sort();
  const complete = !unknown && !conflict;
  const legacyQualification = rows.some(row => row.legacy) ? ' Qualified legacy polygons support screening exposure only, not a native surveyed boundary or negative coverage.' : '';
  const candidates = positive ? rows.filter(row => row.positive) : rows;
  const headline = candidates.slice().sort((a, b) =>
    RISK_ORDER[zoneToRisk(b.zone)] - RISK_ORDER[zoneToRisk(a.zone)] || a.zone.localeCompare(b.zone))[0];
  return {
    zone: positive || complete ? headline?.zone ?? 'UNMAPPED' : rows.length ? 'UNKNOWN' : 'UNMAPPED',
    sfha: positive,
    screeningCoverage: {
      status: complete ? 'complete' : rows.length ? 'partial' : 'unknown',
      observedZones,
      observedSourceRoles: [...new Set(attributes.flatMap(attrs => attrs.SOURCE_ROLE ? [attrs.SOURCE_ROLE] : []))].sort(),
      reason: (conflict ? 'Overlapping FEMA records disagree on SFHA exposure; known positive exposure is retained.'
        : unknown ? 'FEMA mapping is missing, withheld, unclassified, or truncated; absence of observed SFHA does not establish flood-free land.'
        : 'The point has a consistent mapped flood classification.') + legacyQualification,
    },
  };
}

interface FemaCoverageConstraints {
  complete: boolean;
  mapped: FemaPolygonFeature[];
  unavailable: FemaPolygonFeature[];
  conflicts: FemaPolygonFeature[];
  reason: string;
}

/** Coverage proof independent of positive hazard geometry. Malformed rows
 * with no certified envelope withhold completeness for the entire query. */
const UNKNOWN_COVERAGE = (observedZones: Set<string>): FloodScreeningCoverage =>
  ({ status: 'unknown', reason: 'FEMA coverage geometry could not be verified; no complete screening claim is available.', observedZones: [...observedZones].sort() });

function snapConstraints(context: FemaCoverageConstraints | undefined): FemaCoverageConstraints | undefined {
  return context ? { ...context, mapped: context.mapped.map(snapFeaturePrecision), unavailable: context.unavailable.map(snapFeaturePrecision), conflicts: context.conflicts.map(snapFeaturePrecision) } : undefined;
}

/** The sentence a complete-coverage screening emits, rewritten when a tier caps it. */
export const COMPLETE_COVERAGE_SENTENCE = 'The selected area has complete, consistent mapped flood classifications.';
/** Rewrites only the completeness sentence of a coverage reason, keeping every
 * other qualifier (source conflicts, qualified-legacy caveats, context). */
export function withoutCompletenessClaim(reason: string): string {
  return reason.replace(COMPLETE_COVERAGE_SENTENCE, 'Mapped flood classifications observed in the selected area are mutually consistent; completeness is not established.');
}

/** A positive flag that disagrees with, or lacks, a declared chance class
 * cannot retain either the complete or the mutually-consistent claim. */
function withoutClassificationAgreement(reason: string): string {
  return reason.replace(COMPLETE_COVERAGE_SENTENCE, 'Complete coverage is not established from these source classifications.')
    .replace('Mapped flood classifications observed in the selected area are mutually consistent; completeness is not established.',
      'Complete coverage is not established from these source classifications.');
}

/** A fallback tier never claims complete coverage: the completeness epsilon
 * (1e-6 ac) is finer than the precision the fallback computed with. A coverage
 * already disclosed at a lower tier (the coverage embedded by a snapped
 * analysis may itself have needed only the sweep on the snapped inputs) is
 * raised to the tier its inputs carried; the earlier note is kept. */
export function disclosedCoverage(coverage: FloodScreeningCoverage, tier: FemaGeometryTier): FloodScreeningCoverage {
  if (coverage.status === 'unknown') return coverage;
  if (coverage.geometryTier === tier || (coverage.geometryTier === 'precision-snap' && tier === 'single-sweep')) return coverage;
  // A capped coverage must not keep its completeness sentence but keeps every
  // other qualifier; raising 'single-sweep' to 'precision-snap' replaces the
  // earlier note (the sweep note asserts exact-precision inputs, which no
  // longer holds).
  const base = withoutCompletenessClaim(coverage.reason).replace(SINGLE_SWEEP_NOTE, '');
  const reason = base + tierNote(tier);
  return { ...coverage, status: 'partial', geometryTier: tier, ...(tier === 'precision-snap' ? { precisionSnapDeg: GEOMETRY_PRECISION_SNAP_DEG } : {}), reason };
}

export function assessFemaFloodCoverage(
  boundary: FemaBoundaryInput,
  features: ReadonlyArray<FemaPolygonFeature>,
  truncated = false,
  context?: FemaCoverageConstraints,
): FloodScreeningCoverage {
  const exact = assessFemaFloodCoverageAt(boundary, features, truncated, context, false);
  if (exact.status !== 'ring-failure') return exact;
  try {
    const swept = assessFemaFloodCoverageAt(boundary, features, truncated, context, true);
    if (swept.status !== 'ring-failure') return disclosedCoverage(swept, 'single-sweep');
    const snapped = assessFemaFloodCoverageAt(snapBoundary(boundary), features.map(snapFeaturePrecision), truncated, snapConstraints(context), true);
    if (snapped.status !== 'ring-failure') return disclosedCoverage(snapped, 'precision-snap');
  } catch {
    // fall through to the closed result
  }
  return UNKNOWN_COVERAGE(new Set(exact.observedZones));
}

/** Internal: 'ring-failure' is a private sentinel status that never escapes
 * assessFemaFloodCoverage; every other exception maps to 'unknown'. */
function assessFemaFloodCoverageAt(
  boundary: FemaBoundaryInput,
  features: ReadonlyArray<FemaPolygonFeature>,
  truncated: boolean,
  context: FemaCoverageConstraints | undefined,
  sweep: boolean,
): FloodScreeningCoverage | { status: 'ring-failure'; observedZones: string[] } {
  const observedZones = new Set<string>();
  const sourceRoles = new Set<string>();
  let malformed = truncated || context?.complete === false;
  const mapped: MultiPolygon[] = [], positive: MultiPolygon[] = [], negative: MultiPolygon[] = [], masks: MultiPolygon[] = [];
  try {
    const parsedParcel = toParcelMultiPolygon(boundary, sweep);
    if (!parsedParcel) throw new Error('invalid parcel');
    const { geom: parcel, acres } = parsedParcel;
    for (const feature of features) {
      try {
        const attrs = feature.attributes ?? {};
        if (attrs.SOURCE_ROLE) sourceRoles.add(attrs.SOURCE_ROLE);
        // Diagnostic invalid geometry is not a validated unavailable envelope.
        if (attrs.nativeValid === false && attrs.STATUS !== 'Source_Unavailable') { malformed = true; continue; }
        const raw = feature.geometry?.rings;
        const geometry = raw ? arcgisRingsToMultiPolygon(raw) : null;
        if (!geometry) { malformed = true; continue; }
        const clipped = polygonClipping.intersection(parcel, geometry) as MultiPolygon;
        if (multiPolygonAreaAcres(clipped) <= AREA_EPSILON_ACRES) continue;
        if (isFemaSourceUnavailable(attrs)) { masks.push(clipped); continue; }
        const row = floodClassification(attrs);
        observedZones.add(row.zone);
        if (row.known && !row.legacy) mapped.push(clipped);
        // An unclassified overlay also withholds a negative coverage claim.
        else if (!row.known) masks.push(clipped);
        if (row.positive) positive.push(clipped);
        if (row.negative && !row.legacy) negative.push(clipped);
      } catch { malformed = true; }
    }
    const clipContext = (rows: FemaPolygonFeature[]): MultiPolygon[] => rows.map(feature => {
      const rings = feature.geometry?.rings ?? [];
      const geometry = arcgisRingsToMultiPolygon(rings);
      if (!geometry) {
        // An exact-cell intersection can leave a structurally valid ring set
        // with zero planar area. That footprint covers no acreage, so its
        // parcel intersection is empty and it withholds nothing. Only
        // malformed context geometry fails the whole assessment closed.
        if (isZeroAreaRingSet(rings)) return [];
        throw new Error('Invalid FEMA context geometry');
      }
      return polygonClipping.intersection(parcel, geometry) as MultiPolygon;
    });
    if (context) masks.push(...clipContext(context.unavailable));
    const mask = unionMany(masks, sweep);
    let mappedUnion = unionMany(mapped, sweep);
    if (context) {
      const declaredMapped = unionMany(clipContext(context.mapped), sweep);
      mappedUnion = mappedUnion.length && declaredMapped.length
        ? polygonClipping.intersection(mappedUnion, declaredMapped) as MultiPolygon : [];
    }
    const trustedMapped = malformed ? [] : mask.length && mappedUnion.length
      ? polygonClipping.difference(mappedUnion, mask) as MultiPolygon : mappedUnion;
    const positiveUnion = unionMany(positive, sweep), negativeUnion = unionMany(negative, sweep);
    const observedConflict = positiveUnion.length && negativeUnion.length
      ? polygonClipping.intersection(positiveUnion, negativeUnion) as MultiPolygon : [];
    const conflicts = unionMany([observedConflict, ...(context ? clipContext(context.conflicts) : [])], sweep);
    const mappedFraction = clampFraction(multiPolygonAreaAcres(trustedMapped) / acres);
    const unavailableFraction = malformed ? 1 : clampFraction(multiPolygonAreaAcres(mask) / acres);
    const conflictFraction = clampFraction(multiPolygonAreaAcres(conflicts) / acres);
    const unknownGeometry = trustedMapped.length
      ? polygonClipping.difference(parcel, trustedMapped) as MultiPolygon : parcel;
    const unknownAcres = multiPolygonAreaAcres(unknownGeometry);
    const complete = !malformed && unknownAcres <= AREA_EPSILON_ACRES
      && unavailableFraction <= AREA_EPSILON_ACRES / acres && conflictFraction <= AREA_EPSILON_ACRES / acres;
    return {
      status: complete ? 'complete' : mapped.length || positive.length ? 'partial' : 'unknown',
      mappedFraction: round(mappedFraction, 8), unavailableFraction: round(unavailableFraction, 8),
      conflictFraction: round(conflictFraction, 8), observedZones: [...observedZones].sort(), observedSourceRoles: [...sourceRoles].sort(),
      reason: (complete ? COMPLETE_COVERAGE_SENTENCE
        : `${malformed ? 'Malformed or truncated source records prevent complete screening. ' : ''}${unavailableFraction > 0 ? 'Source-unavailable or unclassified coverage is withheld. ' : ''}${conflictFraction > 0 ? 'Overlapping FEMA records disagree on SFHA exposure. ' : ''}${unknownAcres > AREA_EPSILON_ACRES ? 'Unmapped land remains unknown. ' : ''}Known positive flood polygons are retained; remaining acreage is not confirmed flood-free.`) + (context && !context.complete ? ` ${context.reason}` : '') + (sourceRoles.has('legacy-screening-derivative') ? ' Qualified legacy polygons support screening exposure only, not native surveyed boundaries or negative coverage.' : ''),
    };
  } catch (error) {
    if (isClippingRingFailure(error)) return { status: 'ring-failure', observedZones: [...observedZones].sort() };
    return UNKNOWN_COVERAGE(observedZones);
  }
}

function parseDatum(raw: string | undefined): FloodData['verticalDatum'] {
  const s = raw?.toUpperCase() ?? '';
  if (s.includes('NAVD')) return 'NAVD88';
  if (s.includes('NGVD')) return 'NGVD29';
  if (s.includes('MLLW')) return 'MLLW';
  return undefined;
}

function parseEffectiveDate(raw: number | string | undefined): string | undefined {
  if (raw == null) return undefined;
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  if (typeof raw === 'string' && trimmed === '') return undefined;
  const millis = typeof raw === 'number'
    ? raw
    : /^\d{11,}$/.test(trimmed)
      ? Number(trimmed)
      : Date.parse(trimmed);
  if (!Number.isFinite(millis)) return undefined;
  return new Date(millis).toISOString().slice(0, 10);
}

function parseStaticBfe(
  raw: number | string | undefined,
  rawUnit: string | undefined,
): number | undefined {
  if (raw == null) return undefined;
  if (typeof raw === 'string' && raw.trim() === '') return undefined;
  const value = Number(raw);
  if (!Number.isFinite(value) || Math.abs(value) === 9_999) {
    return undefined;
  }
  const unit = rawUnit?.trim().toUpperCase() ?? '';
  const feet = unit === 'FEET' || unit === 'FOOT' || unit === 'FT'
    ? value
    : unit === 'METERS' || unit === 'METER' || unit === 'M'
      ? value * 3.280839895013123
      : undefined;
  if (feet == null || feet < -100 || feet > 20_000) return undefined;
  return round(feet, 3);
}

function composePanelId(attrs: FemaAttributes): string | undefined {
  const direct = attrs.FIRM_PAN?.trim();
  if (direct) return direct;
  if (attrs.DFIRM_ID && attrs.PANEL) return `${attrs.DFIRM_ID}${attrs.PANEL}`;
  // DFIRM_ID identifies a study database, not a FIRM panel. Never render it
  // as a panel number when the polygon layer does not directly supply PANEL.
  return undefined;
}

function oneUnique<T>(values: Array<T | undefined>): T | undefined {
  const unique = [...new Set(values.filter((v): v is T => v !== undefined))];
  return unique.length === 1 ? unique[0] : undefined;
}

/** Metadata covering a parcel is unambiguous only when every intersecting
 * feature supplies the same valid value. A single populated record beside an
 * empty/conflicting record is not promoted to parcel-wide evidence. */
function oneUniqueComplete<T>(values: Array<T | undefined>): T | undefined {
  if (values.length === 0 || values.some(value => value === undefined)) return undefined;
  return oneUnique(values);
}

interface IntersectedFeature {
  zone: string;
  subtype: string;
  sfha: boolean;
  floodway: boolean;
  annualChance: FloodAnnualChanceClass;
  geom: MultiPolygon;
  attrs: FemaAttributes;
}

/**
 * Pure parcel/NFHL geometry analysis. Returns `null` when any returned feature
 * lacks usable polygon geometry; callers must use the labeled sample fallback
 * rather than presenting a partial polygon response as exact.
 */
export function analyzeFemaFloodGeometry(
  boundary: FemaBoundaryInput,
  features: ReadonlyArray<FemaPolygonFeature>,
): FloodData | null {
  try {
    return analyzeFemaFloodGeometryUnsafe(boundary, features, false);
  } catch (error) {
    // polygon-clipping rejects self-intersections and other malformed ring
    // topology. Exact acreage must fail closed so the caller can use the
    // explicitly labelled sample-sweep estimate. The one retried case is the
    // library's own ring-reconstruction failure on near-coincident segments:
    // first the same set union in a single sweep at exact precision, then on
    // the fixed precision grid. Both are disclosed, capped at partial
    // coverage, and floored by the exact per-feature positive exposure.
    if (!isClippingRingFailure(error)) return null;
    try {
      let tier: FemaGeometryTier = 'single-sweep';
      let result: FloodData | null;
      try {
        result = analyzeFemaFloodGeometryUnsafe(boundary, features, true);
      } catch (sweepError) {
        if (!isClippingRingFailure(sweepError)) return null;
        tier = 'precision-snap';
        result = analyzeFemaFloodGeometryUnsafe(snapBoundary(boundary), features.map(snapFeaturePrecision), true);
      }
      if (!result?.areaAnalysis) return null;
      return withExactPositiveFloor(disclosedAnalysis(result, tier), boundary, features);
    } catch {
      return null;
    }
  }
}

function disclosedAnalysis(result: FloodData, tier: FemaGeometryTier): FloodData {
  const areaAnalysis: FloodAreaAnalysis = { ...result.areaAnalysis!, geometryTier: tier, ...(tier === 'precision-snap' ? { precisionSnapDeg: GEOMETRY_PRECISION_SNAP_DEG } : {}) };
  // The coverage embedded by the fallback analysis was computed on the same
  // fallback inputs; disclose and cap it the same way.
  const screeningCoverage = result.screeningCoverage ? disclosedCoverage(result.screeningCoverage, tier) : result.screeningCoverage;
  // The fail-closed headline rule inside the analysis saw the uncapped
  // coverage; re-derive it against the capped one so a fallback run without
  // positive acreage never carries a definite negative zone label.
  const withheld = !result.isSpecialFloodHazard && screeningCoverage?.status !== 'complete' && result.femaZone !== 'UNMAPPED';
  return { ...result, areaAnalysis, screeningCoverage, ...(withheld ? { femaZone: 'UNKNOWN', floodRisk: 'Low-Medium' as const } : {}) };
}

/** The fallback tiers may erase or understate positive exposure. Exact
 * per-feature clips do not need the failing unions, so the result is floored
 * by them: the SFHA flag, the risk and the headline zone follow the
 * worst-risk positive feature that exactly intersects the parcel, and the
 * 1%-annual-chance acreage (plus the floodway acreage when applicable) is
 * floored to the LARGEST exactly clipped positive feature, recorded in
 * exactPositiveFloorAcres. The floored acreage is moved into that feature's
 * zone row out of unmapped land first and then out of the lowest-risk rows,
 * so sum(zone fractions) + unmapped stays closed and the whole-parcel flood
 * controls that gate on closure keep working. Value-gated: it applies
 * whenever the exact clip exceeds what the fallback reported for that zone,
 * even if a hairline positive survived. Exported for its unit test. */
/** Area (acres) that a clipped region can gain or lose when every vertex of
 * the given rings (the parcel and the feature it is clipped against) moves by
 * the grid bound: 2 x total perimeter x max shift. Planar approximation at
 * each edge's latitude. */
export function snapAreaToleranceAcres(rings: ReadonlyArray<ReadonlyArray<Position>>): number {
  const shiftM = Number(GEOMETRY_PRECISION_SNAP_MAX_SHIFT_MM) / 1000;
  const metresPerDegLat = 111_320;
  let perimeterM = 0;
  for (const ring of rings) {
    for (let i = 1; i < ring.length; i++) {
      const [lng0, lat0] = ring[i - 1]!; const [lng1, lat1] = ring[i]!;
      const cosLat = Math.cos(((lat0 + lat1) / 2) * Math.PI / 180);
      perimeterM += Math.hypot((lng1 - lng0) * metresPerDegLat * cosLat, (lat1 - lat0) * metresPerDegLat);
    }
  }
  return (2 * perimeterM * shiftM) / 4046.8564224;
}

export function withExactPositiveFloor(result: FloodData, boundary: FemaBoundaryInput, features: ReadonlyArray<FemaPolygonFeature>): FloodData {
  const parsedParcel = toParcelMultiPolygon(boundary, false) ?? toParcelMultiPolygon(boundary, true);
  if (!parsedParcel) return result;
  const { geom: parcelGeom, rings: parcelRings } = parsedParcel;
  let worstRisk: string | undefined;
  let largest: { zone: string; acres: number; floodway: boolean; rings: MultiPolygon } | undefined;
  let largestZeroPointTwo: { zone: string; acres: number; floodway: boolean; rings: MultiPolygon } | undefined;
  const otherPositiveZones = new Set<string>();
  const undeterminedPositiveZones = new Set<string>();
  let largestFloodwayAcres = 0;
  let largestFloodwayRings: MultiPolygon = [];
  let skippedFeatures = 0;
  for (const feature of features) {
    const attrs = feature.attributes ?? {};
    if (isFemaSourceUnavailable(attrs) || !floodClassification(attrs).positive) continue;
    try {
      const geometry = feature.geometry?.rings ? arcgisRingsToMultiPolygon(feature.geometry.rings) : null;
      if (!geometry) continue;
      const acres = multiPolygonAreaAcres(polygonClipping.intersection(parcelGeom, geometry) as MultiPolygon);
      if (acres <= AREA_EPSILON_ACRES) continue;
      const subtype = String(attrs.ZONE_SUBTY ?? '');
      const zone = normalizeFloodZone(attrs.FLD_ZONE, subtype);
      const floodway = isFloodway(subtype);
      if (!worstRisk || RISK_ORDER[zoneToRisk(zone)] > RISK_ORDER[zoneToRisk(worstRisk)]) worstRisk = zone;
      // The 1% floor takes the feature's OWN annual-chance class, not its SFHA
      // flag. A 0.2%-annual-chance feature carrying SFHA_TF='T' — the conflict
      // case floodClassification models as positive AND negative — would
      // otherwise be booked into the 1% acreage and emitted as a
      // '1-percent'/sfha:true zone row while the 0.2% acreage stayed 0.
      const chance = annualChanceClass(zone, subtype, false);
      const isOnePercent = chance === '1-percent';
      if (isOnePercent && (!largest || acres > largest.acres)) largest = { zone, acres, floodway, rings: geometry };
      if (!isOnePercent) otherPositiveZones.add(zone);
      if (chance === 'undetermined') undeterminedPositiveZones.add(zone);
      if (chance === '0.2-percent' && (!largestZeroPointTwo || acres > largestZeroPointTwo.acres)) {
        largestZeroPointTwo = { zone, acres, floodway, rings: geometry };
      }
      if (floodway && acres > largestFloodwayAcres) { largestFloodwayAcres = acres; largestFloodwayRings = geometry; }
    } catch {
      // An exact clip that itself fails cannot lower the result, but the floor
      // is then computed over a subset and says so.
      skippedFeatures += 1;
    }
  }
  const area = result.areaAnalysis;
  // The skipped count is disclosed on every path, including "no floor".
  const withSkipped = (data: FloodData): FloodData => skippedFeatures > 0 && data.areaAnalysis
    ? { ...data, areaAnalysis: { ...data.areaAnalysis, exactPositiveFloorSkippedFeatures: skippedFeatures } }
    : data;
  if (!worstRisk) return withSkipped(result);
  const femaZone = !result.isSpecialFloodHazard && (RISK_ORDER[zoneToRisk(worstRisk)] > RISK_ORDER[zoneToRisk(result.femaZone)] || result.femaZone === 'UNKNOWN') ? worstRisk : result.femaZone;
  const headline = { isSpecialFloodHazard: true, floodRisk: 'High' as const, femaZone };
  // Keep an SFHA-positive source flag even when its own zone declares another
  // chance class. The flag is conflicting evidence, not permission to book
  // that feature as 1% acreage. Independent class totals may overlap; the
  // exclusive zone mix must still close and must not erase a higher-risk row.
  const withOtherPositiveClasses = (data: FloodData): FloodData => {
    if (otherPositiveZones.size === 0) return data;
    const sourceArea = data.areaAnalysis;
    let nextArea = sourceArea;
    let classFloor = '';
    if (sourceArea && largestZeroPointTwo) {
      const candidate = largestZeroPointTwo;
      const tolerance = sourceArea.geometryTier === 'precision-snap'
        ? Math.max(AREA_EPSILON_ACRES, snapAreaToleranceAcres([...parcelRings, ...candidate.rings.flatMap(polygon => polygon.map(ring => ring as Position[]))]))
        : AREA_EPSILON_ACRES;
      const deficit = candidate.acres - sourceArea.zeroPointTwoPercentAnnualChanceAcres;
      if (deficit > tolerance) {
        const parcelAcres = sourceArea.parcelAreaAcres > 0 ? sourceArea.parcelAreaAcres : multiPolygonAreaAcres(parcelGeom);
        const fromUnmapped = Math.min(deficit, Math.max(0, sourceArea.unmappedAreaAcres));
        let remaining = deficit - fromUnmapped;
        const existing = sourceArea.zoneAreas.find(row => row.zone === candidate.zone && row.annualChance === '0.2-percent');
        const rows = sourceArea.zoneAreas.filter(row => row !== existing).map(row => ({ ...row }))
          .sort((a, b) => RISK_ORDER[zoneToRisk(a.zone)] - RISK_ORDER[zoneToRisk(b.zone)]);
        for (const row of rows) {
          if (remaining <= AREA_EPSILON_ACRES) break;
          if (row.sfha || row.annualChance === '1-percent' || RISK_ORDER[zoneToRisk(row.zone)] > RISK_ORDER[zoneToRisk(candidate.zone)]) continue;
          const take = Math.min(remaining, Math.max(0, row.acres));
          row.acres -= take; remaining -= take;
        }
        const restored = (existing?.acres ?? 0) + deficit - Math.max(0, remaining);
        if (restored > AREA_EPSILON_ACRES) rows.push({
          zone: candidate.zone, annualChance: '0.2-percent', acres: restored,
          fraction: clampFraction(restored / parcelAcres), sfha: false,
          floodway: candidate.floodway || existing?.floodway === true,
        });
        const unmappedAcres = Math.max(0, sourceArea.unmappedAreaAcres - fromUnmapped);
        nextArea = {
          ...sourceArea,
          zoneAreas: rows.filter(row => row.acres > AREA_EPSILON_ACRES)
            .map(row => ({ ...row, acres: round(row.acres), fraction: round(clampFraction(row.acres / parcelAcres), 8) }))
            .sort((a, b) => RISK_ORDER[zoneToRisk(b.zone)] - RISK_ORDER[zoneToRisk(a.zone)]),
          mappedAreaAcres: round(parcelAcres - unmappedAcres), unmappedAreaAcres: round(unmappedAcres),
          mappedFraction: round(clampFraction((parcelAcres - unmappedAcres) / parcelAcres), 8),
          unmappedFraction: round(clampFraction(unmappedAcres / parcelAcres), 8),
          zeroPointTwoPercentAnnualChanceAcres: round(candidate.acres),
          zeroPointTwoPercentAnnualChanceFraction: round(clampFraction(candidate.acres / parcelAcres), 8),
        };
        classFloor = ` The 0.2% annual-chance acreage retains a lower bound of ${smallAcres(candidate.acres)} from the largest exactly clipped feature declaring that class. This independent class bound may overlap higher-risk evidence; the exclusive zone mix is not summed with it.`;
      }
    }
    if (nextArea && !largest) {
      const tolerance = nextArea.geometryTier === 'precision-snap'
        ? Math.max(AREA_EPSILON_ACRES, snapAreaToleranceAcres([...parcelRings, ...largestFloodwayRings.flatMap(polygon => polygon.map(ring => ring as Position[]))]))
        : AREA_EPSILON_ACRES;
      if (largestFloodwayAcres - nextArea.floodwayAcres > tolerance) {
        nextArea = { ...nextArea, floodwayAcres: round(largestFloodwayAcres),
          floodwayFraction: round(clampFraction(largestFloodwayAcres / nextArea.parcelAreaAcres), 8),
          exactFloodwayFloorAcres: round(largestFloodwayAcres) };
      }
    }
    const classified = [...otherPositiveZones].filter(zone => !undeterminedPositiveZones.has(zone)).sort();
    const classification = (classified.length > 0 ? ` Zone ${classified.join(', ')} has a positive flag conflicting with its declared annual-chance classification.` : '')
      + (undeterminedPositiveZones.size > 0 ? ` The declared annual-chance classification remains undetermined for Zone ${[...undeterminedPositiveZones].sort().join(', ')}.` : '');
    const disclosure = `An exact per-feature clip retained SFHA_TF=T for Zone ${[...otherPositiveZones].sort().join(', ')}.${classification} The positive source flag is retained for conservative screening; it does not establish 1% annual-chance acreage or complete coverage.${classFloor}`;
    const coverage = data.screeningCoverage ?? UNKNOWN_COVERAGE(otherPositiveZones);
    const reason = withoutClassificationAgreement(coverage.reason);
    return {
      ...data, ...headline,
      ...(nextArea ? {
        areaAnalysis: { ...nextArea, exactPositiveClassFloorDisclosure: disclosure },
        zoneOverlap: [
          ...nextArea.zoneAreas.map(row => ({ zone: row.zone, fraction: row.fraction })),
          ...(nextArea.unmappedAreaAcres > AREA_EPSILON_ACRES ? [{ zone: 'UNMAPPED', fraction: nextArea.unmappedFraction }] : []),
        ],
      } : {}),
      screeningCoverage: { ...coverage, status: coverage.status === 'unknown' ? 'unknown' : 'partial',
        observedZones: [...new Set([...coverage.observedZones, ...otherPositiveZones])].sort(),
        reason: reason.includes(disclosure) ? reason : `${reason} ${disclosure}` },
    };
  };
  if (!largest) return withOtherPositiveClasses(withSkipped(result));
  if (!area) return withOtherPositiveClasses(withSkipped({ ...result, ...headline }));
  // Like-for-like gates: a single positive feature's exact parcel clip can
  // never exceed the union of all positive clips (the 1% acreage), nor can a
  // single floodway clip exceed the floodway union. A fallback that reports
  // less has lost exposure; an exclusive zone mix that is already right is
  // never rebalanced (a per-feature clip is NOT comparable to an exclusive
  // zone row, so that comparison is not used).
  // On the precision-snap tier the two sides of each gate were computed at
  // different precisions (the exact clip on the original rings, the fallback
  // acreage on snapped parcel AND feature geometry), so the tolerance is the
  // area those boundaries can gain or lose when every vertex moves by the
  // grid bound: twice the summed perimeter of the parcel and the feature,
  // never below the acreage epsilon. On the single-sweep tier both sides are
  // exact and only the acreage epsilon applies.
  const snapped = area.geometryTier === 'precision-snap';
  const ringsOf = (geometry: MultiPolygon): Position[][] => geometry.flatMap(polygon => polygon.map(ring => ring as Position[]));
  const tolerance1pct = snapped ? Math.max(AREA_EPSILON_ACRES, snapAreaToleranceAcres([...parcelRings, ...ringsOf(largest.rings)])) : AREA_EPSILON_ACRES;
  const toleranceFloodway = snapped ? Math.max(AREA_EPSILON_ACRES, snapAreaToleranceAcres([...parcelRings, ...ringsOf(largestFloodwayRings)])) : AREA_EPSILON_ACRES;
  const deficit = largest.acres - area.onePercentAnnualChanceAcres;
  const floodwayDeficit = largestFloodwayAcres - area.floodwayAcres;
  const onePctFloored = deficit > tolerance1pct;
  const floodwayFloored = floodwayDeficit > toleranceFloodway;
  if (!onePctFloored && !floodwayFloored) {
    // A headline restored without any acreage floor is still a disclosed
    // restoration: the record says an exact per-feature clip found exposure.
    return withOtherPositiveClasses(withSkipped(result.isSpecialFloodHazard ? result : { ...result, ...headline, areaAnalysis: { ...area, exactPositiveHeadlineRestored: true } }));
  }
  const parcelAreaAcres = area.parcelAreaAcres > 0 ? area.parcelAreaAcres : multiPolygonAreaAcres(parcelGeom);
  let zoneAreas: FloodZoneArea[] = area.zoneAreas;
  let unmappedAreaAcres = area.unmappedAreaAcres;
  if (onePctFloored) {
    // Move `deficit` acres into the largest feature's zone row: out of
    // unmapped land first, then out of the lowest-risk rows, never below zero,
    // so the zone set stays closed.
    const existing = area.zoneAreas.find(z => z.zone === largest!.zone);
    let remaining = deficit;
    const fromUnmapped = Math.min(remaining, Math.max(0, area.unmappedAreaAcres));
    remaining -= fromUnmapped;
    const rows = area.zoneAreas.filter(z => z.zone !== largest!.zone).map(z => ({ ...z }))
      .sort((a, b) => RISK_ORDER[zoneToRisk(a.zone)] - RISK_ORDER[zoneToRisk(b.zone)]);
    for (const row of rows) {
      if (remaining <= AREA_EPSILON_ACRES) break;
      const take = Math.min(remaining, Math.max(0, row.acres));
      row.acres -= take; remaining -= take;
    }
    // Only acreage actually sourced from unmapped land or lower-risk rows is
    // deposited, so the zone set can never sum past the parcel. (The reported
    // 1% acreage still takes the full exact clip below: that is the adverse
    // direction and is not part of the closure sum.)
    const flooredAcres = (existing?.acres ?? 0) + (deficit - Math.max(0, remaining));
    const flooredRow: FloodZoneArea = {
      zone: largest.zone, annualChance: existing?.annualChance ?? '1-percent', acres: round(flooredAcres), fraction: round(clampFraction(flooredAcres / parcelAreaAcres), 8),
      sfha: true, floodway: largest.floodway || existing?.floodway === true,
    };
    zoneAreas = [...rows.filter(z => z.acres > AREA_EPSILON_ACRES).map(z => ({ ...z, acres: round(z.acres), fraction: round(clampFraction(z.acres / parcelAreaAcres), 8) })), flooredRow]
      .sort((a, b) => RISK_ORDER[zoneToRisk(b.zone)] - RISK_ORDER[zoneToRisk(a.zone)]);
    unmappedAreaAcres = Math.max(0, area.unmappedAreaAcres - fromUnmapped);
  }
  const mappedAreaAcres = Math.max(0, parcelAreaAcres - unmappedAreaAcres);
  const onePercent = Math.max(area.onePercentAnnualChanceAcres, largest.acres);
  const floodway = Math.max(area.floodwayAcres, largestFloodwayAcres);
  const zoneOverlap = zoneAreas.map(z => ({ zone: z.zone, fraction: z.fraction }));
  if (unmappedAreaAcres > AREA_EPSILON_ACRES) zoneOverlap.push({ zone: 'UNMAPPED', fraction: round(clampFraction(unmappedAreaAcres / parcelAreaAcres), 8) });
  return withOtherPositiveClasses({
    ...result, ...headline, zoneOverlap,
    areaAnalysis: {
      ...area, zoneAreas,
      mappedAreaAcres: round(mappedAreaAcres), unmappedAreaAcres: round(unmappedAreaAcres),
      mappedFraction: round(clampFraction(mappedAreaAcres / parcelAreaAcres), 8), unmappedFraction: round(clampFraction(unmappedAreaAcres / parcelAreaAcres), 8),
      onePercentAnnualChanceAcres: round(onePercent), onePercentAnnualChanceFraction: round(clampFraction(onePercent / parcelAreaAcres), 8),
      floodwayAcres: round(floodway), floodwayFraction: round(clampFraction(floodway / parcelAreaAcres), 8),
      // The fallback's own closure error keeps its diagnostic meaning; the
      // rebalance preserves mapped + unmapped by construction.
      closureErrorFraction: area.closureErrorFraction,
      ...(onePctFloored ? { exactPositiveFloorAcres: round(largest.acres) } : {}),
      ...(floodwayFloored ? { exactFloodwayFloorAcres: round(largestFloodwayAcres) } : {}),
      ...(skippedFeatures > 0 ? { exactPositiveFloorSkippedFeatures: skippedFeatures } : {}),
    },
  });
}

/** Acreage that must not render as '0.00 ac' when it is small but non-zero. */
function smallAcres(acres: number): string {
  return acres > 0 && acres < 0.005 ? '< 0.01 ac' : `${acres.toFixed(2)} ac`;
}

/** Reader- and model-facing citation for the polygon path, built from the
 * composed (capped, disclosed) record so it never contradicts it. */
export function polygonFloodSourceText(exact: FloodData, emitted: FloodData, audit: FloodQueryAudit, breakdown: string): string {
  const area = exact.areaAnalysis!;
  const shipped = emitted.screeningCoverage;
  const floorClause = area.exactPositiveFloorAcres !== undefined
    ? `The 1% annual-chance acreage is a lower bound restored from the largest exactly clipped positive feature (${smallAcres(area.exactPositiveFloorAcres)}) after the geometry fallback erased or understated that exposure${area.exactPositiveFloorSkippedFeatures ? `; ${area.exactPositiveFloorSkippedFeatures} positive feature(s) whose own exact clip failed are not in that bound` : ''}. ` : '';
  const skippedClause = (area.exactPositiveFloorAcres === undefined && area.exactPositiveFloorSkippedFeatures
    ? `The exact per-feature positive check could not be completed for ${area.exactPositiveFloorSkippedFeatures} positive feature(s). ` : '')
    + (area.exactPositiveHeadlineRestored ? 'Special Flood Hazard Area exposure was restored from an exact per-feature clip after the geometry fallback reported none. ' : '');
  const floodwayFloorClause = area.exactFloodwayFloorAcres !== undefined
    ? `The regulatory floodway acreage is a lower bound restored from the largest exactly clipped floodway feature (${smallAcres(area.exactFloodwayFloorAcres)}). ` : '';
  const liveTierClause = (audit.liveGeometryTier
    ? `The live NFHL body was admitted on the ${audit.liveGeometryTier} geometry fallback. ` : '') + skippedClause + floodwayFloorClause;
  return (
              `FEMA NFHL ${area.geometryTier ? `parcel-polygon intersection computed with the ${area.geometryTier === 'precision-snap' ? `${GEOMETRY_PRECISION_SNAP_DEG}-degree coordinate-grid` : 'single-sweep union'} geometry fallback after an exact-precision clipping failure` : 'exact parcel-polygon intersection'}${breakdown ? `: ${breakdown}` : ': no mapped flood polygon intersected the parcel'}. ` +
              `1% annual-chance (100-year) ${area.onePercentAnnualChanceAcres.toFixed(2)} ac; ` +
              `0.2% annual-chance (500-year) ${area.zeroPointTwoPercentAnnualChanceAcres.toFixed(2)} ac; ` +
              `regulatory floodway ${area.floodwayAcres.toFixed(2)} ac. ` +
              `Returned classification footprint ${(area.mappedFraction * 100).toFixed(1)}%; ` +
              `${area.unmappedAreaAcres.toFixed(2)} ac (${(area.unmappedFraction * 100).toFixed(1)}%) outside those polygons remains unmapped/unknown, not Zone X. ` +
              (shipped?.mappedFraction !== undefined
                ? `Validated screening coverage ${(shipped.mappedFraction * 100).toFixed(1)}%. ` : '') +
              `${shipped?.reason ?? ''} ` +
              (audit.source === 'refdata-snapshot'
                ? `Snapshot ${audit.snapshotVersion}, source vintage ${audit.snapshotVintageDate}. `
                : `Full NFHL live polygon service.${audit.retainedSnapshotEvidence ? ` Earlier snapshot ${audit.snapshotVersion} evidence and source conflicts are retained.` : ''} `) +
              (audit.liveOutcome === 'unavailable-or-no-coverage-improvement'
                ? 'Full NFHL lookup did not supply more complete usable coverage; known snapshot hazards are retained.'
                : '')
  ).replace(/\s+$/, '') + (floorClause || liveTierClause ? ` ${floorClause}${liveTierClause}`.replace(/\s+$/, '') : '');
}

/** The polygon-path provider record. The audit's coverage is the shipped
 * coverage, but a fallback-tier analysis caps and discloses it too, and the
 * fail-closed headline guard reads the coverage that is actually emitted.
 * Exported for its unit test. */
export function composePolygonFloodData(exact: FloodData, audit: FloodQueryAudit): FloodData {
  const tier = exact.areaAnalysis?.geometryTier;
  const shipped = audit.screeningCoverage ?? exact.screeningCoverage;
  let screeningCoverage = shipped && tier ? disclosedCoverage(shipped, tier) : shipped;
  const classDisclosure = exact.areaAnalysis?.exactPositiveClassFloorDisclosure;
  if (classDisclosure) {
    const retained = screeningCoverage ?? UNKNOWN_COVERAGE(new Set(exact.screeningCoverage?.observedZones ?? []));
    const reason = withoutClassificationAgreement(retained.reason);
    const sourceReason = withoutClassificationAgreement(exact.screeningCoverage?.reason ?? classDisclosure);
    screeningCoverage = { ...retained,
      status: retained.status === 'unknown' || exact.screeningCoverage?.status === 'unknown' ? 'unknown' : 'partial',
      observedZones: [...new Set([...retained.observedZones, ...(exact.screeningCoverage?.observedZones ?? [])])].sort(),
      reason: reason.includes(sourceReason) ? reason : `${reason} ${sourceReason}` };
  }
  // The fail-closed headline withhold is keyed on the AUDIT coverage as
  // before (a missing audit coverage withholds the zone); the analysis's own
  // embedded coverage is only ever a fallback for what is emitted.
  const auditCoverage = audit.screeningCoverage && tier ? disclosedCoverage(audit.screeningCoverage, tier) : audit.screeningCoverage;
  return {
    ...exact,
    ...(audit.regulatoryContext ? { regulatoryContext: audit.regulatoryContext } : {}),
    ...(!exact.isSpecialFloodHazard && auditCoverage?.status !== 'complete'
      ? { femaZone: exact.femaZone === 'UNMAPPED' ? 'UNMAPPED' : 'UNKNOWN', floodRisk: 'Low-Medium' as const } : {}),
    ...(screeningCoverage ? { screeningCoverage } : {}),
    queryAudit: audit,
  };
}

function analyzeFemaFloodGeometryUnsafe(
  boundary: FemaBoundaryInput,
  features: ReadonlyArray<FemaPolygonFeature>,
  sweep: boolean,
): FloodData | null {
  const parsedParcel = toParcelMultiPolygon(boundary, sweep);
  if (!parsedParcel) return null;
  const { geom: parcelGeom, acres: parcelAreaAcres } = parsedParcel;

  const intersected: IntersectedFeature[] = [];
  for (const feature of features) {
    if (isFemaSourceUnavailable(feature.attributes ?? {})) continue;
    const rawRings = feature.geometry?.rings;
    if (!rawRings) return null;
    const featureGeom = arcgisRingsToMultiPolygon(rawRings);
    if (!featureGeom) return null;
    const geom = polygonClipping.intersection(parcelGeom, featureGeom) as MultiPolygon;
    if (multiPolygonAreaAcres(geom) <= AREA_EPSILON_ACRES) continue;
    const attrs = feature.attributes ?? {};
    const subtype = String(attrs.ZONE_SUBTY ?? '');
    const zone = normalizeFloodZone(attrs.FLD_ZONE, subtype);
    const sfha = floodClassification(attrs).positive;
    intersected.push({
      zone,
      subtype,
      sfha,
      floodway: isFloodway(subtype),
      annualChance: annualChanceClass(zone, subtype, sfha),
      geom,
      attrs,
    });
  }

  const mappedUnion = unionMany(intersected.map(f => f.geom), sweep);
  const mappedAreaAcres = Math.min(parcelAreaAcres, multiPolygonAreaAcres(mappedUnion));
  const unmappedGeom = mappedUnion.length > 0
    ? polygonClipping.difference(parcelGeom, mappedUnion) as MultiPolygon
    : parcelGeom;
  const unmappedAreaAcres = Math.min(parcelAreaAcres, multiPolygonAreaAcres(unmappedGeom));

  const onePercentGeom = unionMany(
    intersected.filter(f => f.annualChance === '1-percent').map(f => f.geom), sweep,
  );
  const zeroPointTwoGeom = unionMany(
    intersected.filter(f => f.annualChance === '0.2-percent').map(f => f.geom), sweep,
  );
  const floodwayGeom = unionMany(intersected.filter(f => f.floodway).map(f => f.geom), sweep);
  const onePercentAcres = Math.min(parcelAreaAcres, multiPolygonAreaAcres(onePercentGeom));
  const zeroPointTwoAcres = Math.min(parcelAreaAcres, multiPolygonAreaAcres(zeroPointTwoGeom));
  const floodwayAcres = Math.min(parcelAreaAcres, multiPolygonAreaAcres(floodwayGeom));

  const grouped = new Map<string, IntersectedFeature[]>();
  for (const feature of intersected) {
    const group = grouped.get(feature.zone) ?? [];
    group.push(feature);
    grouped.set(feature.zone, group);
  }
  const orderedGroups = [...grouped.entries()].sort(([zoneA], [zoneB]) => {
    const riskDiff = RISK_ORDER[zoneToRisk(zoneB)] - RISK_ORDER[zoneToRisk(zoneA)];
    return riskDiff !== 0 ? riskDiff : zoneA.localeCompare(zoneB);
  });

  let allocated: MultiPolygon = [];
  const allocatedGroups: MultiPolygon[] = [];
  let exclusiveMappedAreaAcres = 0;
  const zoneAreas: FloodZoneArea[] = [];
  for (const [zone, group] of orderedGroups) {
    const groupGeom = unionMany(group.map(f => f.geom), sweep);
    const exclusive = allocated.length > 0
      ? polygonClipping.difference(groupGeom, allocated) as MultiPolygon
      : groupGeom;
    const acres = Math.min(parcelAreaAcres, multiPolygonAreaAcres(exclusive));
    if (acres > AREA_EPSILON_ACRES) {
      exclusiveMappedAreaAcres += acres;
      zoneAreas.push({
        zone,
        annualChance: group.some(f => f.annualChance === '1-percent')
          ? '1-percent'
          : group.some(f => f.annualChance === '0.2-percent')
            ? '0.2-percent'
            : group[0]!.annualChance,
        acres: round(acres),
        fraction: round(clampFraction(acres / parcelAreaAcres), 8),
        sfha: group.some(f => f.sfha),
        floodway: group.some(f => f.floodway),
      });
    }
    if (sweep) {
      // Re-union every allocated group in one sweep rather than folding onto
      // the previous partial result (see unionMany).
      allocatedGroups.push(groupGeom);
      allocated = unionMany(allocatedGroups, true);
    } else {
      allocated = allocated.length > 0
        ? polygonClipping.union(allocated, groupGeom) as MultiPolygon
        : groupGeom;
    }
  }

  const mappedClosureError = Math.abs(mappedAreaAcres + unmappedAreaAcres - parcelAreaAcres) / parcelAreaAcres;
  const exclusiveClosureError = Math.abs(exclusiveMappedAreaAcres + unmappedAreaAcres - parcelAreaAcres) / parcelAreaAcres;
  const closureErrorFraction = Math.max(mappedClosureError, exclusiveClosureError);
  if (!Number.isFinite(closureErrorFraction) || closureErrorFraction > AREA_CLOSURE_TOLERANCE) {
    return null;
  }

  const zoneOverlap = zoneAreas.map(z => ({ zone: z.zone, fraction: z.fraction }));
  if (unmappedAreaAcres > AREA_EPSILON_ACRES) {
    zoneOverlap.push({
      zone: 'UNMAPPED',
      fraction: round(clampFraction(unmappedAreaAcres / parcelAreaAcres), 8),
    });
  }
  const realZones = zoneAreas.slice().sort((a, b) =>
    RISK_ORDER[zoneToRisk(b.zone)] - RISK_ORDER[zoneToRisk(a.zone)],
  );
  const coverage = assessFemaFloodCoverage(boundary, features);
  const observedHeadlineZone = realZones[0]?.zone ?? 'UNMAPPED';
  const headlineZone = onePercentAcres <= AREA_EPSILON_ACRES && coverage.status !== 'complete' && observedHeadlineZone !== 'UNMAPPED' ? 'UNKNOWN' : observedHeadlineZone;

  const metadataAttrs = intersected.map(f => f.attrs);
  const uniqueBfe = oneUniqueComplete(
    metadataAttrs.map(attrs => parseStaticBfe(attrs.STATIC_BFE, attrs.LEN_UNIT)),
  );
  const uniqueDatum = uniqueBfe !== undefined
    ? oneUniqueComplete(metadataAttrs.map(attrs => parseDatum(attrs.V_DATUM)))
    : undefined;
  const uniquePanel = oneUniqueComplete(metadataAttrs.map(composePanelId));
  const uniqueEffectiveDate = oneUniqueComplete(metadataAttrs.map(attrs => parseEffectiveDate(attrs.EFF_DATE)));

  const areaAnalysis: FloodAreaAnalysis = {
    method: 'polygon-intersection',
    parcelAreaAcres: round(parcelAreaAcres),
    mappedAreaAcres: round(mappedAreaAcres),
    unmappedAreaAcres: round(unmappedAreaAcres),
    mappedFraction: round(clampFraction(mappedAreaAcres / parcelAreaAcres), 8),
    unmappedFraction: round(clampFraction(unmappedAreaAcres / parcelAreaAcres), 8),
    onePercentAnnualChanceAcres: round(onePercentAcres),
    onePercentAnnualChanceFraction: round(clampFraction(onePercentAcres / parcelAreaAcres), 8),
    zeroPointTwoPercentAnnualChanceAcres: round(zeroPointTwoAcres),
    zeroPointTwoPercentAnnualChanceFraction: round(clampFraction(zeroPointTwoAcres / parcelAreaAcres), 8),
    floodwayAcres: round(floodwayAcres),
    floodwayFraction: round(clampFraction(floodwayAcres / parcelAreaAcres), 8),
    zoneAreas,
    closureErrorFraction: round(closureErrorFraction, 10),
  };

  return {
    femaZone: headlineZone,
    isSpecialFloodHazard: onePercentAcres > AREA_EPSILON_ACRES,
    floodRisk: onePercentAcres > AREA_EPSILON_ACRES ? 'High' : headlineZone === 'UNMAPPED' ? 'Low-Medium' : zoneToRisk(headlineZone),
    screeningCoverage: coverage,
    zoneOverlap,
    analysisMethod: 'polygon-intersection',
    areaAnalysis,
    ...(uniqueBfe !== undefined ? { staticBfeFt: uniqueBfe } : {}),
    ...(uniqueDatum ? { verticalDatum: uniqueDatum } : {}),
    ...(uniquePanel ? { firmPanelId: uniquePanel } : {}),
    ...(uniqueEffectiveDate ? { effectiveDate: uniqueEffectiveDate } : {}),
  };
}

/** Query the NFHL for one point. Returns `null` only after every endpoint
 * fails; caller cancellation is always re-thrown rather than degraded. */
async function queryFemaPoint(
  lat: number,
  lng: number,
  signal?: AbortSignal,
): Promise<FemaPointResult | null> {
  throwIfAborted(signal);

  // RD-1 (C4) — local-first point containment against the NFHL snapshot.
  // Empty containment remains unknown. Exact companion coverage is required
  // before a snapshot may establish a mapped negative. When this runs
  // inside the sample sweep after a snapshot miss, the negative cache makes
  // these probes free.
  const local = await tryRefdata('fema-nfhl', async () => {
    const res = await refdataContains('fema-nfhl', { lat, lng });
    return res;
  });
  if (local) {
    const context = await femaSourceContext(local, { point: { lat, lng } });
    throwIfAborted(signal);
    const projection = projectFemaContext(context);
    const summary = summarizeFemaPoint([
      ...local.features.map(feature => feature.props as FemaAttributes),
      ...projection.legacy.map(feature => feature.attributes),
      ...projection.constraints.unavailable.map(() => ({ STATUS: 'Source_Unavailable' })),
    ], local.truncated !== false || !projection.constraints.complete || projection.constraints.mapped.length === 0,
    projection.constraints.conflicts.length > 0);
    if (!projection.constraints.complete) summary.screeningCoverage.reason += ` ${context.reason}`;
    summary.screeningCoverage = { ...summary.screeningCoverage, sourceKind: 'refdata-snapshot', sourceAuthority: local.sourceAuthority, sourceVersion: local.version, sourceVintageDate: local.vintageDate };
    summary.regulatoryContext = projectFemaRegulatoryContext(context, { point: { lat, lng } });
    return summary;
  }
  throwIfAborted(signal);

  if (isPrivateRefdataAcceptanceDataset('fema-nfhl')) throw new Error('Private FEMA acceptance snapshot did not return point evidence; live supplementation is withheld');

  // Keep geometry as an unescaped `lng,lat` pair. FEMA accepts this form and
  // the stable URL is useful in provider diagnostics / recorded fixtures.
  const params =
    `geometry=${lng},${lat}&geometryType=esriGeometryPoint&inSR=4326` +
    '&spatialRel=esriSpatialRelIntersects&outFields=FLD_ZONE,ZONE_SUBTY,SFHA_TF&f=json';
  for (const endpoint of NFHL_POINT_ENDPOINTS) {
    try {
      const res = await fetchWithTimeout(`${endpoint}?${params}`, 20_000, signal);
      if (!res.ok) continue;
      const text = await res.text();
      throwIfAborted(signal);
      if (text.startsWith('<') || !text.startsWith('{')) continue;
      const json = JSON.parse(text) as { features?: FemaPolygonFeature[]; error?: unknown; exceededTransferLimit?: boolean };
      if (json.error) continue;
      if (!Array.isArray(json.features)) continue;
      return summarizeFemaPoint(json.features.map(feature => feature.attributes ?? {}), json.exceededTransferLimit === true);
    } catch (error) {
      rethrowIfAborted(signal, error);
      continue;
    }
  }
  return null;
}

function boundingBox(poly: ReadonlyArray<{ lat: number; lng: number }>): {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
} {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const p of poly) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  }
  return { minLat, maxLat, minLng, maxLng };
}

function pointInPolygon(lat: number, lng: number, poly: ReadonlyArray<{ lat: number; lng: number }>): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    const intersect =
      (a.lat > lat) !== (b.lat > lat) &&
      lng < ((b.lng - a.lng) * (lat - a.lat)) / (b.lat - a.lat) + a.lng;
    if (intersect) inside = !inside;
  }
  return inside;
}

function pointInBoundaryInput(lat: number, lng: number, boundary: FemaBoundaryInput): boolean {
  if (isParcelPolygonInputArray(boundary)) {
    return boundary.some(
      part =>
        pointInPolygon(lat, lng, part.polygon) &&
        !(part.holes ?? []).some(hole => pointInPolygon(lat, lng, hole)),
    );
  }
  return pointInPolygon(lat, lng, boundary);
}

function sweepPoints(
  boundary: FemaBoundaryInput,
  centroid: { lat: number; lng: number },
): { lat: number; lng: number }[] {
  const { minLat, maxLat, minLng, maxLng } = boundingBox(boundaryPoints(boundary));
  const grid: { lat: number; lng: number }[] = [];
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      grid.push({
        lat: minLat + (maxLat - minLat) * (i / 2),
        lng: minLng + (maxLng - minLng) * (j / 2),
      });
    }
  }
  const insideGrid = grid.filter(p => pointInBoundaryInput(p.lat, p.lng, boundary));
  const base = insideGrid.length >= 3 ? insideGrid : grid;
  return [
    centroid,
    ...base.filter(p => Math.abs(p.lat - centroid.lat) > 1e-9 || Math.abs(p.lng - centroid.lng) > 1e-9),
  ];
}

/** RD-1 (C4) — snapshot feature → the FemaPolygonFeature shape the
 *  clip/dissolve/closure pipeline consumes. GeoJSON ring nesting flattens
 *  into the Esri-style ring list; arcgisRingsToMultiPolygon reassembles
 *  outer/hole roles from winding + containment exactly as for live rows. */
function refdataFeatureToFema(feature: {
  geom: { type: string; coordinates: unknown };
  props: Record<string, string | number | null>;
}): FemaPolygonFeature | null {
  let rings: number[][][] | null = null;
  if (feature.geom.type === 'Polygon') {
    rings = feature.geom.coordinates as number[][][];
  } else if (feature.geom.type === 'MultiPolygon') {
    rings = (feature.geom.coordinates as number[][][][]).flat();
  }
  if (!rings || rings.length === 0) return { attributes: feature.props as FemaAttributes };
  return { attributes: feature.props as FemaAttributes, geometry: { rings } };
}


/** Context supplies coverage restrictions and separately qualified positives.
 * A mapped footprint alone never creates a Zone X or hazard classification. */
function projectFemaContext(context: FemaSourceContextAssessment): {
  constraints: FemaCoverageConstraints; legacy: FemaPolygonFeature[];
} {
  const convert = (kind: keyof FemaSourceContextAssessment['byKind']) => context.byKind[kind]
    .map(refdataFeatureToFema).filter((feature): feature is FemaPolygonFeature => feature != null);
  const candidates = convert('qualified-legacy-sfha');
  const legacy = candidates.filter(feature => !isFemaSourceUnavailable(feature.attributes)
    && floodClassification(feature.attributes).positive)
    .map(feature => ({ ...feature, attributes: { ...feature.attributes, SOURCE_ROLE: 'legacy-screening-derivative' as const } }));
  return {
    legacy,
    constraints: {
      complete: context.canAssessCoverage && legacy.length === candidates.length,
      mapped: convert('native-mapped'),
      unavailable: [...convert('native-unresolved'), ...convert('native-unavailable'), ...convert('legacy-unavailable')],
      conflicts: convert('source-conflict'),
      reason: legacy.length === candidates.length ? context.reason : 'Qualified legacy context contains an unsupported hazard classification.',
    },
  };
}

export async function queryFemaPolygons(
  boundary: FemaBoundaryInput,
  signal?: AbortSignal,
): Promise<{ features: FemaPolygonFeature[]; queryAudit: FloodQueryAudit } | { fallbackReason: string }> {
  throwIfAborted(signal);
  const allPoints = boundaryPoints(boundary);
  const box = boundingBox(allPoints);
  if (![box.minLat, box.maxLat, box.minLng, box.maxLng].every(Number.isFinite)) {
    return { fallbackReason: 'parcel geometry is malformed' };
  }

  // Reduced NFHL snapshots can omit Zone X. An empty/partial result cannot
  // establish coverage outside the returned hazard polygons. Try the full live
  // layer before retaining the snapshot's explicit UNMAPPED remainder.
  const local = await tryRefdata('fema-nfhl', () =>
    refdataBbox('fema-nfhl', box, 2000));
  const fp = isParcelPolygonInputArray(boundary)
    ? multiBoundaryFingerprint(boundary)
    : boundaryFingerprint(boundary);
  const audit: FloodQueryAudit = {
    source: 'refdata-snapshot',
    boundaryFingerprint: fp ?? '',
    ...(local ? {
      snapshotVersion: local.version,
      snapshotVintageDate: local.vintageDate,
      snapshotAuthority: local.sourceAuthority,
    } : {}),
  };
  let best: { features: FemaPolygonFeature[]; queryAudit: FloodQueryAudit } | undefined;
  let bestMappedFraction = -1;
  let sourceConstraints: FemaCoverageConstraints | undefined;
  if (local) {
    const context = await femaSourceContext(local, { point: allPoints[0]!, polygon: allPoints });
    throwIfAborted(signal);
    const projection = projectFemaContext(context);
    audit.regulatoryContext = projectFemaRegulatoryContext(
      context,
      isParcelPolygonInputArray(boundary)
        ? { point: allPoints[0]!, polygons: boundary }
        : { point: allPoints[0]!, polygon: boundary },
    );
    sourceConstraints = { ...projection.constraints, complete: projection.constraints.complete && local.truncated === false };
    audit.femaContext = { status: context.status, reason: context.reason, ...(context.binding ? { binding: context.binding } : {}),
      counts: Object.fromEntries(Object.entries(context.byKind).map(([kind, rows]) => [kind, rows.length])) };
    const features = [...local.features.map(refdataFeatureToFema)
      .filter((f): f is FemaPolygonFeature => f != null), ...projection.legacy];
    const coverage = assessFemaFloodCoverage(boundary, features, local.truncated, sourceConstraints);
    bestMappedFraction = coverage.mappedFraction ?? 0;
    audit.localMappedFraction = bestMappedFraction;
    audit.screeningCoverage = coverage;
    if (local.truncated) audit.truncated = true;
    if (features.length > 0 || !local.truncated) best = { features, queryAudit: audit };
    if (isPrivateRefdataAcceptanceDataset('fema-nfhl')) return { features, queryAudit: audit };
    if (best && coverage.status === 'complete') return best;
  }
  throwIfAborted(signal);
  if (isPrivateRefdataAcceptanceDataset('fema-nfhl')) throw new Error('Private FEMA acceptance snapshot did not return polygon evidence; live supplementation is withheld');
  audit.liveAttemptedAt = new Date().toISOString();
  audit.liveOutcome = 'unavailable-or-no-coverage-improvement';

  const params = new URLSearchParams({
    where: '1=1',
    geometry: `${box.minLng},${box.minLat},${box.maxLng},${box.maxLat}`,
    geometryType: 'esriGeometryEnvelope',
    inSR: '4326',
    outSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
    outFields: NFHL_OUT_FIELDS,
    returnGeometry: 'true',
    geometryPrecision: '7',
    resultRecordCount: '2000',
    f: 'json',
  });
  let liveTransferLimited = false;
  for (const endpoint of NFHL_ENDPOINTS) {
    try {
      const res = await fetchWithTimeout(`${endpoint}?${params.toString()}`, 20_000, signal);
      if (!res.ok) continue;
      const text = await res.text();
      throwIfAborted(signal);
      if (text.startsWith('<') || !text.startsWith('{')) continue;
      const json = JSON.parse(text) as {
        features?: FemaPolygonFeature[];
        exceededTransferLimit?: boolean;
        error?: unknown;
      };
      if (json.exceededTransferLimit) liveTransferLimited = true;
      if (json.error || !Array.isArray(json.features) || (json.exceededTransferLimit && json.features.length === 0)) continue;
      // A body whose exact analysis fails is admitted only on a disclosed
      // fallback tier (its coverage can never claim complete); the tier is
      // recorded on the audit so the source-selection deviation is visible.
      const liveAnalysis = json.features.length === 0 ? null : analyzeFemaFloodGeometry(boundary, json.features);
      if (!liveAnalysis) continue;
      const liveGeometryTier = liveAnalysis.areaAnalysis?.geometryTier;
      // Keep the worst tier seen across endpoints on the shared audit: the
      // candidate set accumulates every admitted body, so the disclosure must too.
      if (liveGeometryTier && (!audit.liveGeometryTier || liveGeometryTier === 'precision-snap')) audit.liveGeometryTier = liveGeometryTier;
      // A new service body cannot erase an earlier positive, source conflict,
      // or unavailable-source mask. Preserve the actual frozen source records.
      // No geometry is repaired or assigned authority from larger coverage.
      const candidate = [...(best?.features ?? []), ...json.features.map(feature => ({
        ...feature,
        attributes: { ...feature.attributes, SOURCE_ROLE: 'official-live' as const },
      }))];
      const truncated = audit.truncated === true || json.exceededTransferLimit === true || best?.queryAudit.truncated === true;
      const coverage = assessFemaFloodCoverage(boundary, candidate, truncated, sourceConstraints);
      const improved = (coverage.mappedFraction ?? 0) > bestMappedFraction;
      bestMappedFraction = coverage.mappedFraction ?? 0;
      best = { features: candidate, queryAudit: {
        ...audit,
        source: 'live-nfhl',
        truncated,
        retainedSnapshotEvidence: (local?.features.length ?? 0) > 0,
        screeningCoverage: coverage,
        liveEndpoint: endpoint,
        liveOutcome: improved ? 'more-complete-coverage' : 'additional-source-evidence',
        ...(audit.liveGeometryTier ? { liveGeometryTier: audit.liveGeometryTier } : {}),
      } };
      if (coverage.status === 'complete') return best;
    } catch (error) {
      rethrowIfAborted(signal, error);
      continue;
    }
  }
  if (best) return best;
  return { fallbackReason: local?.truncated
    ? 'NFHL snapshot response exceeded its transfer limit'
    : liveTransferLimited ? 'NFHL polygon response exceeded its transfer limit'
    : 'all NFHL polygon endpoints failed or returned unusable geometry' };
}

async function fetchSampleFallback(
  boundary: FemaBoundaryInput,
  centroid: { lat: number; lng: number },
  fallbackReason: string,
  signal?: AbortSignal,
): Promise<ProviderResult<FloodData>> {
  throwIfAborted(signal);
  const points = sweepPoints(boundary, centroid).slice(0, 10);
  const results = await Promise.all(points.map(p => queryFemaPoint(p.lat, p.lng, signal)));
  throwIfAborted(signal);
  const ok = results.filter((r): r is FemaPointResult => r !== null);
  if (ok.length === 0) {
    throw new Error('FEMA NFHL: all sample-point queries failed (FEMA servers may block cloud IPs - verify manually at msc.fema.gov)');
  }
  const counts = new Map<string, number>();
  for (const r of ok) counts.set(r.zone, (counts.get(r.zone) ?? 0) + 1);
  const zoneOverlap = [...counts.entries()]
    .map(([zone, count]) => ({ zone, fraction: count / ok.length }))
    .sort((a, b) => b.fraction - a.fraction);
  const realZones = ok.filter(r => r.zone !== 'UNMAPPED');
  const headline = (realZones.length > 0 ? realZones : ok)
    .slice()
    .sort((a, b) => RISK_ORDER[zoneToRisk(b.zone)] - RISK_ORDER[zoneToRisk(a.zone)])[0]!;
  const anySfha = ok.some(r => r.sfha);
  const floodRisk = anySfha ? 'High' : headline.zone === 'UNMAPPED' ? 'Low-Medium' : zoneToRisk(headline.zone);
  const overlap = zoneOverlap.map(z => `${z.zone} ${(z.fraction * 100).toFixed(0)}%`).join(', ');
  const source = zoneOverlap.length > 1
    ? `FEMA NFHL estimated parcel straddles flood zones: ${overlap} (${ok.length}-point boundary sweep after polygon analysis failed: ${fallbackReason}). Highest risk: Zone ${headline.zone}.`
    : `FEMA NFHL estimated Zone ${headline.zone}, uniform across the parcel samples (${ok.length}-point boundary sweep after polygon analysis failed: ${fallbackReason}).`;
  return {
    data: {
      femaZone: headline.zone,
      isSpecialFloodHazard: anySfha,
      floodRisk,
      zoneOverlap,
      sampledPoints: ok.length,
      analysisMethod: 'sample-sweep',
      screeningCoverage: { status: 'partial', observedZones: [...new Set(ok.flatMap(r => r.screeningCoverage.observedZones))].sort(), reason: 'Point samples do not establish complete parcel coverage; known positive flood exposure is retained.' },
      fallbackReason,
    },
    sources: [{
      field: 'land.floodRisk',
      source,
      url: 'https://msc.fema.gov/portal/home',
      retrievedAt: new Date().toISOString(),
    }],
  };
}

/**
 * Cache contract version. `withCache` reads Firestore BEFORE provider.fetch,
 * and the cache key encodes only LOCATION — so a change in what produces the
 * answer is invisible to it and stale entries shadow refdata indefinitely.
 *
 * Redhead's SED v34 is the proof: `wetlandAcres: 65.7`, source "... (cached)",
 * served from an entry written before the refdata swap, while live refdata at
 * the same coordinates answers from the 0.1-degree snapshot. hifldGrid needed
 * exactly this bump (v2-refdata-snapshot) and got one; the other seven
 * refdata-backed providers did not, which made the whole migration invisible
 * to every existing site until TTL expiry.
 *
 * Bump this string whenever the DATA SOURCE or its shape changes.
 */
const FEMA_FLOOD_CACHE_CONTRACT_VERSION = 'v7-qualified-regulatory-context';

/** Boundary sites key by the polygon fingerprint, point sites by grid cell. */
function femaFloodCacheBase(lat: number, lng: number, geo?: ProviderGeoContext): string {
  const validBoundaries = geo?.boundaries?.filter(b => b.polygon.length >= 3);
  const useMulti =
    validBoundaries &&
    validBoundaries.length > 0 &&
    (validBoundaries.length > 1 ||
      validBoundaries.some(b => (b.holes?.length ?? 0) > 0) ||
      !(geo?.boundary && geo.boundary.length >= 3));
  const fingerprint = useMulti
    ? multiBoundaryFingerprint(validBoundaries)
    : boundaryFingerprint(geo?.boundary);
  return fingerprint ? `boundary-${fingerprint}` : gridCellKey(lat, lng);
}

export const femaFloodProvider: DataProvider<FloodData> = {
  id: 'fema-flood',
  name: 'FEMA National Flood Hazard Layer (NFHL)',
  cacheTtlMs: CACHE_TTL.STANDARD,
  requiresKey: false,

  // ONE cacheKey: the boundary-fingerprint keying (a polygon query caches by
  // the polygon, not its centroid cell) AND the refdata contract version both
  // survive — the version prefixes BOTH key shapes so a snapshot cutover
  // invalidates polygon-keyed entries too, not only point lookups.
  // (Kept in the uniform `cacheKey: (…) => \`${VERSION}_…\`` arrow shape the
  // refdataCacheContract source scan pins.)
  cacheKey: ({ lat, lng, geo }) => `${FEMA_FLOOD_CACHE_CONTRACT_VERSION}_${femaFloodCacheBase(lat, lng, geo)}`,

  async fetch(lat, lng, _apiKey?: string, geo?: ProviderGeoContext): Promise<ProviderResult<FloodData>> {
    throwIfAborted(geo?.signal);
    const validBoundaries = geo?.boundaries?.filter(b => b.polygon.length >= 3);
    const boundary: FemaBoundaryInput | undefined =
      validBoundaries &&
      validBoundaries.length > 0 &&
      (validBoundaries.length > 1 ||
        validBoundaries.some(b => (b.holes?.length ?? 0) > 0) ||
        !(geo?.boundary && geo.boundary.length >= 3))
        ? validBoundaries
        : geo?.boundary && geo.boundary.length >= 3
          ? geo.boundary
          : undefined;
    if (!boundary) {
      const result = await queryFemaPoint(lat, lng, geo?.signal);
      throwIfAborted(geo?.signal);
      if (!result) {
        throw new Error('FEMA NFHL: all endpoints failed (FEMA servers may block cloud IPs - verify manually at msc.fema.gov)');
      }
      const data: FloodData = {
        femaZone: result.zone,
        isSpecialFloodHazard: result.sfha,
        floodRisk: result.sfha ? 'High' : result.zone === 'UNMAPPED' ? 'Low-Medium' : zoneToRisk(result.zone),
        screeningCoverage: result.screeningCoverage,
        ...(result.regulatoryContext ? { regulatoryContext: result.regulatoryContext } : {}),
        sampledPoints: 1,
        analysisMethod: 'point',
      };
      return {
        data,
        sources: [{
          field: 'land.floodRisk',
          source: result.zone === 'UNMAPPED'
            ? 'FEMA NFHL - no flood mapping available for this point'
            : `FEMA NFHL Flood Zone ${result.zone}${result.sfha ? ' (1% annual-chance Special Flood Hazard Area)' : ''} - point query. ${result.screeningCoverage.reason}${result.screeningCoverage.sourceKind === 'refdata-snapshot' ? ` Snapshot ${result.screeningCoverage.sourceVersion}; source ${result.screeningCoverage.sourceAuthority}; vintage ${result.screeningCoverage.sourceVintageDate}.` : ''}`,
          url: 'https://msc.fema.gov/portal/home',
          retrievedAt: new Date().toISOString(),
        }],
      };
    }

    const polygonResult = await queryFemaPolygons(boundary, geo?.signal);
    throwIfAborted(geo?.signal);
    if ('features' in polygonResult) {
      const exact = analyzeFemaFloodGeometry(boundary, polygonResult.features);
      if (exact) {
        const area = exact.areaAnalysis!;
        const breakdown = area.zoneAreas
          .map(z => `${z.zone} ${z.acres.toFixed(2)} ac (${(z.fraction * 100).toFixed(1)}%)`)
          .join(', ');
        const emitted = composePolygonFloodData(exact, polygonResult.queryAudit);
        return {
          data: emitted,
          sources: [{
            field: 'land.floodRisk',
            source: polygonFloodSourceText(exact, emitted, polygonResult.queryAudit, breakdown),
            url: polygonResult.queryAudit.liveEndpoint ?? 'https://msc.fema.gov/portal/home',
            retrievedAt: new Date().toISOString(),
          }],
        };
      }
      if (isPrivateRefdataAcceptanceDataset('fema-nfhl')) throw new Error('Private FEMA acceptance polygon geometry is unusable; sample substitution is withheld');
      const fallback = await fetchSampleFallback(
        boundary,
        geo?.centroid ?? { lat, lng },
        'NFHL returned malformed or non-closing polygon geometry',
        geo?.signal,
      );
      if (polygonResult.queryAudit.regulatoryContext) fallback.data.regulatoryContext = polygonResult.queryAudit.regulatoryContext;
      return fallback;
    }
    if (isPrivateRefdataAcceptanceDataset('fema-nfhl')) throw new Error('Private FEMA acceptance requires a valid polygon query; sample substitution is withheld');
    return fetchSampleFallback(
      boundary,
      geo?.centroid ?? { lat, lng },
      polygonResult.fallbackReason,
      geo?.signal,
    );
  },
};
